import angular from "angular";

import {
  AbsenceCauseCode,
  Calculation, Calculations, Calculator, CalculatorLogic, CalcWorktime, DateRange, Dates, HolidayCode, HolidayYear, MonthlyHolidayAccrual, SalaryDateKind, SalaryDateLogic, Translations, WageBasis, WorkerAbsences, Employments, Employment,
  HolidaysLogic, AnnualLeave,Arrays, InputEnumOption, HolidayPayCalculation, CalculationStatus
} from "@salaxy/core";

import { SessionService, UiHelpers } from "../../services";
import { HolidayYearCrudController, WorkerAbsencesCrudController } from "../worker";
import { CalculatorSections } from "./CalculatorSections";

/**
 * Handles the user interaction of Worktime within the Calculator.
 * The Worktime contains the logic for fetching holidays and absences
 * for that particular period and adding calculation rows for them if necessary.
 */
export class CalcWorktimeController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "UiHelpers",
    "Calculations",
    "Calculator",
    "SessionService",
    "Employments",
    "$timeout"
  ];

  /** User interface shortcuts for period selection */
  public periodShortcuts: {
    /** Text for current month. */
    monthCurrent: string,
    /** Text for last month. */
    monthLast: string,
    /** Text for other month. */
    monthOther: string,
    // monthMulti: "Monta kuukautta",
    /** Text for two weeks. */
    weeks2: string,
    /** Text for half a month */
    monthHalf: string,
    /** Text for other period. */
    other: string,
  };

  /** The calculation that the controller edits (shows in read-only mode). */
  public calc: Calculation;

  /**
   * The employment data for the worker.
   */
  public workerEmployment: Employment;

  /**
   * Worktime data for the period fetched from the server
   */
  public worktime: CalcWorktime;

  /** If there is a validation error in period start, it is added here. */
  public periodStartDateError = null;

  /** Demodata for the anonymous demo calculator. */
  public demoData: {
    /** Start date for which the data was generated. */
    startDate: string,
    /** End date for which the data was generated. */
    endDate: string,
    /** The holiday data */
    holidays?: HolidayYear,
    /** Absences data. */
    absences?: WorkerAbsences,
  } = { startDate: null, endDate: null };

  private _periodStartDate;
  private _dateRange: DateRange;

  /**
   * Creates a new CalcWorktimeController
   * @ignore
   */
  constructor(
    private uiHelpers: UiHelpers,
    private calcApi: Calculations,
    private anonCalcApi: Calculator,
    private sessionService: SessionService,
    private employmentsApi: Employments,
    private $timeout: angular.ITimeoutService,
  ) {
  }

  /**
   * Implement IController
   */
  public $onInit() {
    this.initDateRanges();
    if (this.currentCalc && this.currentCalc.info.workStartDate && this.currentCalc.info.workEndDate) {
      this.reloadHolidays();
    }
  }

  /**
   * Reset the controller to initial state.
   */
  public reset() {
    this._dateRange = {};
    this._periodStartDate = null;
    this.worktime ??= {};
    angular.copy(this.calc?.worktime ?? {}, this.worktime);
  }

  /**
   * Loads the employment data for the current worker.
   */
  public async loadEmployment(): Promise<void> {
    if(this.currentCalc.worker.employmentId) {
      const result = await this.employmentsApi.getSingle(this.currentCalc.worker.employmentId);
      this.workerEmployment = result;
      this.$timeout();

    } else {
      this.workerEmployment = null;
    }
  }

  /**
   * Getter for checking if WorkerHolidays is active.
   */
  public get isWorkerHolidaysActive(): boolean {
    if(this.workerEmployment && this.workerEmployment.holidays) {
      return HolidaysLogic.isWorkerHolidaysActive(this.workerEmployment.holidays);
    }
    return false;
  }

  /** Gets the requested salary date */
  public get requestedSalaryDate(): string {
    return this.calc.workflow.requestedSalaryDate;
  }
  public set requestedSalaryDate(value: string) {
    this.calc.workflow.requestedSalaryDate = value;
    this.calc.workflow.salaryDate = value; // Set the salary date to the requestedSalaryDate until the next recalculation.
  }

  /**
   * Reloads the holidays from server.
   */
  public reloadHolidays(): void {
    this.worktime ??= {};
    if(this.sessionService.isInRole("disableHolidays")) {
      return;
    }
    angular.copy(this.calc.worktime ?? {}, this.worktime);
    if (this.sessionService.isAuthenticated) {
      if (this.calc?.isReadOnly || this.calc?.workflow?.status === CalculationStatus.PaymentStarted) {
        return;
      }
      this.calcApi.getWorktimeData(this.calc).then((result) => angular.copy(result, this.worktime));
    } else {
      this.checkDemoData();
      this.anonCalcApi.getWorktimeData(this.calc, this.demoData.holidays, this.demoData.absences).then((result) => angular.copy(result, this.worktime));
    }
  }

  /** Holiday year has been changed and must be saved and reloaded. */
  public holidaysChanged(holidaysCtrl: HolidayYearCrudController) {
    holidaysCtrl.save().then(() => {
      this.reloadHolidays();
    });
  }

  /** Absences has been changed and must be saved and reloaded. */
  public absencesChanged(absencesCtrl: WorkerAbsencesCrudController) {
    absencesCtrl.save().then(() => {
      this.reloadHolidays();
    });
  }

  /** Gets the current calculation object */
  public get currentCalc(): Calculation {
    return this.calc;
  }

  /**
   * Returns true if there is no net salary payment to worker at all.
   */
  public get isCurrentZeroPayment(): boolean {
      return CalculatorLogic.isZeroPayment(this.calc);
  }

  /** Disable function for the salary date picker. */
  public isDisabledSalaryDate = (date): boolean => {
    const role =  this.sessionService.isInRole("pro") ? "pro" :
                this.sessionService.isInRole("admin") ? "admin" : null;
    switch(this.calc.workflow.salaryDateKind) {
      case SalaryDateKind.InPast:
        return false;
      case SalaryDateKind.Default:
      default:
        return !SalaryDateLogic.isValidSalaryDate(date, null, null, true, this.calc.info.paymentChannel, CalculatorLogic.isZeroPayment(this.calc), role, true);
    }
  };

  /** Getter and setter for Calculation daterange in compatible format for new DateRange component. */
  public get dateRange(): DateRange {
    const info = (this.currentCalc || {}).info;
    const framework = (this.currentCalc || {}).framework;
    if (!this._dateRange) {
      this._dateRange = {
        start: info.workStartDate,
        end: info.workEndDate,
        daysCount: framework.numberOfDays,
      };
    }
    return this._dateRange;
  }
  public set dateRange(value: DateRange) {
    this._dateRange = value;
  }

  /** Returns true if the holidays uses accrual. */
  public get isHolidayAccrual(): boolean {
    if (!this.worktime?.holidaySpec) {
      return false;
    }
    return  this.worktime.holidaySpec.code == HolidayCode.Permanent14Days ||
    this.worktime.holidaySpec.code == HolidayCode.Permanent35Hours ||
    this.worktime.holidaySpec.code == HolidayCode.TemporaryTimeOff ||
    this.worktime.holidaySpec.code == HolidayCode.Other ||
    this.worktime.holidaySpec.code == HolidayCode.Script;
  }


  /** Returns true if holidays are accrued for this calc. */
  public get accruesHolidays(): boolean {
    if (!this.worktime?.accrualSpec) {
      return false;
    }
    return  this.worktime.accrualSpec.code == HolidayCode.Permanent14Days ||
    this.worktime.accrualSpec.code == HolidayCode.Permanent35Hours ||
    this.worktime.accrualSpec.code == HolidayCode.TemporaryTimeOff ||
    this.worktime.accrualSpec.code == HolidayCode.Other ||
    this.worktime.accrualSpec.code == HolidayCode.Script;
  }


  /**
   * Called when the date range changes.
   * @param noPeriodStartDateUpdate If true, the _periodStartDate is not updated.
   * Should be true if the change is triggered by that input to avoid UI flickering.
   */
  public dateRangeChange(noPeriodStartDateUpdate = false) {
    const info = (this.currentCalc || {}).info;
    const framework = (this.currentCalc || {}).framework;
    info.workStartDate = this.dateRange.start;
    if (!noPeriodStartDateUpdate) {
      this._periodStartDate = Dates.format(this.dateRange.start, "D.M.", null);
    }
    info.workEndDate = this.dateRange.end;
    framework.numberOfDays = this.dateRange.daysCount;
    this.reloadHolidays();
  }

  /**
   * Gets or sets the period start date, which is bound to editable input in case the period type
   * is not "other" (date the range / calendar component)
   */
  public get periodStartDate(): string {
    return this._periodStartDate;
  }
  public set periodStartDate(value: string) {
    this._periodStartDate = value;
    const parts = (value || "").split(".");
    if (parts.length < 2 || parts.length > 3) {
      this.periodStartDateError = "SALAXY.NG1.CalcWorktimeComponent.periodStartDateErrorFormat";
      return;
    }
    let year = (parts.length === 3) ? Number((parts[2] || "").trim()) : null;
    if (!year || year < 2019 || year > 2100) {
      year = Number(this.dateRange.start.substr(0, 4));
    }
    const date = Dates.getDate(year, Number(parts[1]), Number(parts[0]));
    if (date) {
      this.periodStartDateError = null;
      switch (this.periodShortcut) {
        case "monthCurrent":
        case "monthLast":
        case "monthOther":
          this.dateRange = Dates.getDateRange(date, Dates.add(Dates.add(date,1,"month"),-1,"day"));
          break;
        case "weeks2":
          this.dateRange = Dates.getDateRange(date, Dates.add(Dates.add(date,2,"week"),-1,"day"));
          break;
        case "monthHalf":
          if (Dates.getDay(date) === 15 || Dates.getDay(date) === 16) {
            this.dateRange = Dates.getDateRange(date, Dates.startEnd(date, "end-month"));
          } else {
            this.dateRange = Dates.getDateRange(date, Dates.add(date,14,"day"));
          }
          break;
        case "other":
          this.dateRange = Dates.getDateRange(date, date);
          break;
      }
      this.dateRangeChange(true);
    } else {
      this.periodStartDateError = value + " " + Translations.get("SALAXY.NG1.CalcWorktimeComponent.periodStartDateErrorNotDate");
    }
  }

  /**
   * Gets the user interface shortcut for the period type:
   * Different user interfaces are shown depending on the period shortcut.
   */
  public get periodShortcut(): "monthCurrent" | "monthLast" | "monthOther" | "weeks2" | "monthHalf" | "other" {
    if (!this.dateRange.start || !this.dateRange.end) {
      return null;
    }
    if (this.dateRange.start === Dates.getDate("today", "today", 1)
      && this.dateRange.end === Dates.startEnd("today", "end-month")) {

      return "monthCurrent";
    }
    if (this.dateRange.start === Dates.add(Dates.startEnd("today","start-month"),-1,"month")
      && this.dateRange.end === Dates.startEnd(Dates.add(Dates.startEnd("today", "start-month"),-1,"month"),"end-month")) {
      return "monthLast";
    }
    if (this.dateRange.end === Dates.add(Dates.add(this.dateRange.start, 1, "month"), -1, "day")) {
      return "monthOther";
    }
    if (this.dateRange.end === Dates.add(Dates.add(this.dateRange.start,2, "week"),-1, "day")) {
      return "weeks2";
    }

    // if the range is 15 (14) days or
    // starts 15th or 16th and ends month end
    if ((Dates.getDuration(this.dateRange.start, this.dateRange.end).days() === 14)
      || (Dates.getDay(this.dateRange.start) === 15 && this.dateRange.end === Dates.startEnd(this.dateRange.start,"end-month"))
      || (Dates.getDay(this.dateRange.start) === 16 && this.dateRange.end === Dates.startEnd(this.dateRange.start,"end-month"))) {
      return "monthHalf";
    }
    return "other";
  }

  public set periodShortcut(value: "monthCurrent" | "monthLast" | "monthOther" | "weeks2" | "monthHalf" | "other") {
    this.periodStartDateError = null;
    switch (value) {
      case "monthCurrent":
        this.dateRange.start = Dates.startEnd("today","start-month");
        this.dateRange.end = Dates.startEnd("today","end-month");
        break;
      case "monthOther":
        if (Dates.getDay("today") === 1) {
          // Move to tomorrow so that the selection is monthOther and not monthCurrent.
          this.dateRange.start = Dates.add("today",1,"day");
        } else {
          this.dateRange.start = Dates.asDate("today");
        }
        this.dateRange.end = Dates.add(Dates.add(this.dateRange.start,1,"month"),-1,"day");
        break;
      case "monthLast":
        this.dateRange.start = Dates.add(Dates.startEnd("today","start-month"),-1,"month");
        this.dateRange.end = Dates.startEnd(this.dateRange.start,"end-month");
        break;
      case "weeks2": //TODO Review this
        this.dateRange.start = Dates.add(Dates.startEnd("today","start-week"),-2,"week"); //shift to start of current week, and then 2 weeks before
        this.dateRange.end = Dates.add(Dates.startEnd("today","start-week"),-1,"day"); //shift to last day of previous week.
        break;
      case "monthHalf":
        if (Dates.getDay("today") < 16) {
          // 16 - (28,29,30,31)
          this.dateRange.start = Dates.add(Dates.add(Dates.startEnd("today", "start-month"),-1,"month"),15,"day");
          this.dateRange.end = Dates.startEnd(this.dateRange.start,"end-month");
        } else {
          // 1 - 15
          this.dateRange.start = Dates.startEnd("today","start-month");
          this.dateRange.end = Dates.add(this.dateRange.start,14,"day");
        }
        break;
      case "other":
        this.dateRange.start = Dates.add("today",-1,"day");
        this.dateRange.end = Dates.asDate("today");
        break;
    }
    this.dateRange = Dates.getDateRange(this.dateRange.start, this.dateRange.end);
    this.dateRangeChange();
  }

  /**
   * Submit the data in the Worktime (period) dialog.
   */
  public submitWorktime() {
    const loader = this.uiHelpers.showLoading("SALAXY.NG1.CalcWorktimeComponent.submitWorktimeTitle");
      angular.copy(this.worktime, this.calc.worktime);
      this.calcApi.recalculateWorktime(this.calc).then((result) => {
        angular.copy(result, this.calc);
        angular.copy(this.calc.worktime, this.worktime);
        loader.dismiss();
        new CalculatorSections(this.currentCalc).setActive("salary");
    });
  }

  /** Returns true if the current worktime data seems to have holidays or absences to add as rows in the salary calculation.  */
  public hasHolidaysOrAbsences() {

    return this.worktime?.holidaySpec?.code === HolidayCode.HolidayCompensation
      || this.worktime?.holidaySpec?.code === HolidayCode.HolidayCompensationIncluded
      || (this.worktime?.absencesPeriods ?? []).length > 0
      ;
  }

  /**
   * Returns the logic for the worktime data related lists
   * @returns Logic object for the lists.
   */
  public getLogicForLists(): any {
    const logic = {
      getAbcencesForPeriod: () => {
        return this.worktime.absencesPeriods.filter(x => x.causeCode != AbsenceCauseCode.AnnualLeave);
      },
      getAbcensePeriodsTotalDays: (type: "all" | "absencesPaid" | "absencesUnpaid" | "absencesHolidayAccrual" | "absencesNoHolidayAccrual" = "all") => {
        const list = logic.getAbcencesForPeriod();
        switch (type) {
          case "all":
            return Arrays.sum(list, (x) => x.period.daysCount);
          case "absencesPaid":
            return Arrays.sum(list.filter((x) => x.isPaid), (x) => x.period.daysCount);
          case "absencesUnpaid":
            return Arrays.sum(list.filter((x) => !x.isPaid), (x) => x.period.daysCount);
          case "absencesHolidayAccrual":
            return Arrays.sum(list.filter((x) => x.isHolidayAccrual), (x) => x.period.daysCount);
          case "absencesNoHolidayAccrual":
            return Arrays.sum(list.filter((x) => !x.isHolidayAccrual), (x) => x.period.daysCount);
        }
      },
      getAnnualLeavesForPeriod: () => {
        return this.worktime.absencesPeriods.filter(x => x.causeCode == AbsenceCauseCode.AnnualLeave);
      },
      getAnnualLeaveDescription: (row: AnnualLeave) => {
        if (row.notes) {
          return row.notes;
        }
        return row.period.start < `${Dates.getYear(this.worktime.workDays.start)}-10-1` ? Translations.get("SALAXY.NG1.CalcWorktimeComponent.lists.annualLeaves.summerHoliday") : Translations.get("SALAXY.NG1.CalcWorktimeComponent.lists.annualLeaves.winterHoliday");
      },
      getAnnualLeavesTotalDays: () => {
        const list = logic.getAnnualLeavesForPeriod();
        return Arrays.sum(list, (x) => x.period.daysCount);
      },
      getAnnualLeavesBonusDays: () => {
        const list = logic.getAnnualLeavesForPeriod();
        return Arrays.sum(list, (x) => x.bonusDaysCount ?? 0);
      },
      getholidayPayCalculationOptions:(): InputEnumOption[] => {
        return this.uiHelpers.cache(this, "holidayPayCalculationOptions", () => {
          switch (this.worktime.holidaySpec?.code) {
            case HolidayCode.Permanent14Days:
              if(this.worktime.holidaySpec?.wageBasis === WageBasis.Monthly) {
                return [
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.normalMonthlyPay.label"), value: HolidayPayCalculation.NormalMonthlyPay},
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                  ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
                ];
              } else {
                return [
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.averageDailyPay.label"), value: HolidayPayCalculation.AverageDailyPay},
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.averageHourlyPay.label"), value: HolidayPayCalculation.AverageHourlyPay},
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                  ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
                ];
              }
            case HolidayCode.Permanent35Hours:
              if(this.worktime.holidaySpec?.wageBasis === WageBasis.Monthly) {
                return [
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.normalMonthlyPay.label"), value: HolidayPayCalculation.NormalMonthlyPay},
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.percentageBasedPay.label"), value: HolidayPayCalculation.PercentageBasedPay},
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                  ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
                ];
              } else {
                return [
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.percentageBasedPay.label"), value: HolidayPayCalculation.PercentageBasedPay},
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                  ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
                ];
              }
            case HolidayCode.TemporaryTimeOff:
              if(this.worktime.holidaySpec?.wageBasis === WageBasis.Monthly || this.worktime.holidaySpec?.wageBasis === WageBasis.PerformanceBased || this.worktime.holidaySpec?.wageBasis === WageBasis.Hourly) {
                return [
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.holidayCompensation.label"), value: HolidayPayCalculation.HolidayCompensation},
                  {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                  ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
                ];
              }
              break;
              case HolidayCode.Other:
              case HolidayCode.Script:
                if(this.worktime.holidaySpec?.wageBasis === WageBasis.Monthly) {
                  return [
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.normalMonthlyPay.label"), value: HolidayPayCalculation.NormalMonthlyPay},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.percentageBasedPay.label"), value: HolidayPayCalculation.PercentageBasedPay},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.holidayCompensation.label"), value: HolidayPayCalculation.HolidayCompensation},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                    ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
                  ];
                } else {
                  return [
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.averageDailyPay.label"), value: HolidayPayCalculation.AverageDailyPay},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.averageHourlyPay.label"), value: HolidayPayCalculation.AverageHourlyPay},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.percentageBasedPay.label"), value: HolidayPayCalculation.PercentageBasedPay},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.holidayCompensation.label"), value: HolidayPayCalculation.HolidayCompensation},
                    {text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.otherPay.label"), value: HolidayPayCalculation.OtherPay},
                    ...(this.sessionService.isInRole("bizConfigs") ? [{text: Translations.get("SALAXY.ENUM.HolidayPayCalculation.scriptPay.label"), value: HolidayPayCalculation.ScriptPay}] : []),
                  ];
                }
                break;
            default:
              return [];
          }
        });
      }
    };
    return logic;
  }

  /** Shows the pro-dialog */
  public showPeriodDetails() {
    const detailsModel = {
      worktime: this.worktime,
      logic: this.getLogicForLists()
    };
    this.uiHelpers.showDialog("salaxy-components/calc/details/worktime-pro.html", null, detailsModel, null, "lg");
  }

  /**
   * Gets the status for the current holidays and absences fetching.
   * These are fetched from the server when the period and / or worker changes.
   */
  public getHolidaysStatus(): "noWorker" | "noPeriod" | "loading" | "ok" {
    if (!this.currentCalc.worker?.employmentId && this.sessionService.isAuthenticated) {
      return "noWorker";
    }
    if (!this.currentCalc.info.workStartDate || !(this.currentCalc.framework.numberOfDays > 0)) {
      return "noPeriod";
    }
    return this.worktime?.holidaySpec == null ? "loading" : "ok";
  }

  /**
   * Gets the monthly text for calendar month for accruals.
   * @param type Type of the text is either header or description.
   */
  public getAccrualMonthText(type: "header" | "description", month: MonthlyHolidayAccrual) {
    if (type === "header") {
      (month as any) = Dates.getMonth(month.month);
    }
    switch (type) {
      case "header":
        return month ? Translations.get(`SALAXY.ENUM.CalendarMonth.month${month}.label`) : "#err month";
      case "description": {
        switch (this.worktime.accrualSpec.code) {
          case HolidayCode.Permanent14Days:
            return "SALAXY.NG1.CalcWorktimeComponent.getAccrualMonthText.permanent14Days";
          case HolidayCode.Permanent35Hours:
            return "SALAXY.NG1.CalcWorktimeComponent.getAccrualMonthText.permanent35Hours";
          case HolidayCode.TemporaryTimeOff:
            return "SALAXY.NG1.CalcWorktimeComponent.getAccrualMonthText.temporaryTimeOff";
          case HolidayCode.Other:
            return "SALAXY.NG1.CalcWorktimeComponent.getAccrualMonthText.other";
          case HolidayCode.Script:
            return "SALAXY.NG1.CalcWorktimeComponent.getAccrualMonthText.script";
          default:
            return "SALAXY.NG1.CalcWorktimeComponent.getAccrualMonthText.default";
        }
      }
    }
    return "#err " + type;
  }

  /**
   * Gets the accrual explanation text. This is currently not in use.
   * TODO: translate
   */
  public getAccrualText(monthlyAccrual: MonthlyHolidayAccrual): string {
    if (this.worktime.accrualSpec.code === HolidayCode.Permanent14Days) {
      if (monthlyAccrual.workDays < 14) {
        return `${monthlyAccrual.workDays} pv < 14 pv => Ei lomakertymää.`;
      } else {
        return `${monthlyAccrual.workDays} pv > 14 pv => Lomakertymä ${monthlyAccrual.daysAccrued} pv.`;
      }
    } else if (this.worktime.accrualSpec.code === HolidayCode.Permanent35Hours) {
      if (monthlyAccrual.workHours == null) {
        return `Ei tietoa tunneista, oletus yli 35h => Lomakertymä ${monthlyAccrual.daysAccrued} pv.`;
      } else if (monthlyAccrual.workHours < 35) {
        return `${monthlyAccrual.workHours} h < 35 h => Ei lomakertymää.`;
      } else {
        return `${monthlyAccrual.workHours} h > 35 h => Lomakertymä ${monthlyAccrual.daysAccrued} pv.`;
      }
    } else if (this.worktime.accrualSpec.code === HolidayCode.TemporaryTimeOff) {
      return `Vapaan ansaintasäännössä lomapäivien kertymä on aina ${monthlyAccrual.daysAccrued} pv / kalenterikuukausi.`;
    } else if (this.worktime.accrualSpec.code === HolidayCode.Other) {
      return Translations.get("SALAXY.ENUM.HolidayCode.other.description");
    } else if (this.worktime.accrualSpec.code === HolidayCode.Script) {
      return Translations.get("SALAXY.ENUM.HolidayCode.script.description");
    } else {
      return "Ei lomapäivien kertymää.";
    }
  }

  /**
   * Opens the dialog for editing calculation payments
   */
  public openCalcLeavesDialog(): void {
    const logic = {calc: this.currentCalc, worktime: this.worktime};
    this.uiHelpers.openEditDialog("salaxy-components/modals/calc/CalcLeaves.html", logic, null, "lg").then(dialogResult => {
      if (dialogResult.result) {
        this.worktime.absencesPeriods = dialogResult.result.absencesPeriods;
      }
    });
  }

  /** Checks if the period has changed and if so resets the demo data. */
  public checkDemoData(): void {
    if (
      this.currentCalc.info.workStartDate == this.demoData.startDate && this.currentCalc.info.workEndDate == this.demoData.endDate) {
      return;
    }
    let holiday = null;
    let absenceDay = null;
    if (this.currentCalc.info.workStartDate && this.currentCalc.info.workEndDate) {
        const workdays = Dates.getWorkdays(this.currentCalc.info.workStartDate, this.currentCalc.info.workEndDate);
        if (workdays.length > 5) {
          holiday = workdays[Math.round(workdays.length / 2)];
          absenceDay = workdays[Math.round(workdays.length / 2) + 1];
        }
    }
    this.demoData = this.demoData || {} as any;
    this.demoData.startDate = this.currentCalc.info.workStartDate;
    this.demoData.endDate = this.currentCalc.info.workEndDate;

    if (!holiday) {
      this.demoData.holidays = null;
      this.demoData.absences = null;
      return;
    }
    this.demoData.holidays = {
      year: Dates.getYear(holiday),
      employmentId: null,
      spec: {
        wageBasis: WageBasis.Monthly,
      }
    };
    this.demoData.absences = {
      employmentId: null,
      periods: [
        {
          causeCode: AbsenceCauseCode.Illness,
          notes: "Poissaolo demodatassa",
          period: {
            start: absenceDay,
            end: absenceDay,
            daysCount: 1,
            days: absenceDay,
          },
        }
      ]
    };
  }

  private initDateRanges() {
    const currentMonth = Dates.getMonth(Dates.getToday());
    this.periodShortcuts = {
      monthCurrent: Translations.get(`SALAXY.ENUM.CalendarMonth.month${currentMonth}.label`),
      monthLast: Translations.get(`SALAXY.ENUM.CalendarMonth.month${currentMonth === 1 ? 12 : (currentMonth - 1)}.label`),
      monthOther: Translations.get("SALAXY.NG1.DateRange.otherMonth"),
      // monthMulti: "Monta kuukautta",
      weeks2: Translations.get("SALAXY.UI_Terms.week2"),
      monthHalf: Translations.get("SALAXY.UI_Terms.monthHalf"),
      other: Translations.get("SALAXY.UI_Terms.otherPeriod"),
    };
    this._periodStartDate = Dates.format(this.dateRange.start, "D.M.", null);
  }

  /** Programmatic tab change via a UI link text. */
  public changeTab(tabId: string) {
    const tabButton = document.querySelector(`#tabs-buttons-calcWorktime #${tabId}-tab-button`) as HTMLElement;
    tabButton ? tabButton.click() : "";
  }
}
